import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment-timezone";
import { Window } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { useDispatch } from "react-redux";

import { DialogComponent } from "src/components/dialog";
import InfoBanner from "../InfoBanner/InfoBanner";
import { MonthCalendar } from "src/components/MultiDateTimeCalendar";
import { METRIC } from "src/components/MultiDateTimeCalendar/calendarUtils";
import { generateHeader, POSTAPI } from "src/api-setup/api-helper";
import { SELECT_MOBILE_USER_DETAILS } from "src/api-setup/api-endpoints";
import { getUserCredential } from "src/lib/cookies";
import { convertTimeZone } from "src/lib/date-time.util";
import { setNewMessage } from "src/redux/actions/setting-actions";
import { selectMobileConfigCode } from "src/pages/messages/service/selectMobileConfigCode";
import { API_CONSTANTS } from "../../../providerPayRequestDashboard/utils/constants";
import { getUserTimezone } from "../../../providerPayRequestDashboard/utils/dateUtils";
import {
  pickProfile,
  positionedRows,
  profileLabel,
  showProfileControl,
} from "../../../providerPayRequestDashboard/utils/selectedProfile";

import ContractualDetailsSection from "./ContractualDetailsSection";
import OptionalDetailsSection from "./OptionalDetailsSection";
import TransactionalDetailsSection from "./TransactionalDetailsSection";
import { useAddPayRequestForm } from "./useAddPayRequestForm";
import { getPayRequestErrors } from "./payRequestValidation";
import {
  createBulkPayRequest,
  deleteTimesheetDocument,
  getAvailablePayRequestDates,
  getTimesheetDocuments,
  getTimesheetListByProvider,
  getWorkCalendarForContract,
  buildPayRequestDataMap,
  updateTimesheet,
  uploadTimesheetDocument,
} from "../../services";
import {
  DEFAULT_DATE_FORMAT,
  formatTimeDisplay,
  parseTimeInput,
  TIME_ZONE,
  TimesheetStatus,
} from "../../utils/payRequestConstants";
import "./AddPayRequest.scss";

const HOURS_METRIC = { text: "Total Hours", value: METRIC.HOURS };
const QUANTITY_METRIC = { text: "Total Quantity(s)", value: METRIC.QUANTITY };
const EMPTY_MAP = new Map();
const MONTH_FETCH_DEBOUNCE_MS = 300;

// Statuses past the editable stage: a match here is an "approved" duplicate.
const LOCKED_STATUS_KEYS = [
  TimesheetStatus.APPROVAL_IN_PROCESS.mstr_key,
  TimesheetStatus.PENDING_COMP_ADMIN_REVIEW.mstr_key,
  TimesheetStatus.HOLD.mstr_key,
  TimesheetStatus.READY_FOR_PAYROLL_PROCESSING.mstr_key,
  TimesheetStatus.SENT_TO_PAYROLL.mstr_key,
];

const DUPLICATE_MESSAGES = {
  PENDING:
    "Pending Pay request already exists on the same date with same details. Please review/edit the existing request to avoid duplication.",
  APPROVED:
    "Approved Pay request already exists on the same date with same details. Do you still want to create a new one?",
};

const ATTESTATION_TEXT =
  "I attest to the accuracy of this submission and affirm that all information provided to the best of my knowledge is true and correct.";

/*
 * Add / Edit Pay Request. Web port of the mobile AddPayRequestNew screen: the
 * contractual selections and the calendar on the left, the resulting rate
 * breakdown and the optional details on the right.
 */
const AddPayRequestDialog = ({
  visible,
  onClose,
  onSaved,
  providerId,
  orgPositionKey = 0,
  task = null,
  onNavigateToEdit,
  defaultPositionKey = null,
}) => {
  const dispatch = useDispatch();
  const cookiesData = useMemo(() => getUserCredential(), []);
  const isEdit = !!task;
  const [profileRows, setProfileRows] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [editPositionLabel, setEditPositionLabel] = useState(null);
  const activeProviderId = selectedProfile?.provider_mstr_key ?? providerId;

  const [currentMonth, setCurrentMonth] = useState(() =>
    task?.ts_date ? moment.tz(task.ts_date, TIME_ZONE).tz(moment.tz.guess()).toDate() : new Date()
  );
  // Debounced copy of currentMonth: the grid repaints instantly, the network
  // calls wait for the user to settle on a month.
  const [fetchMonth, setFetchMonth] = useState(currentMonth);

  const [selectedDates, setSelectedDates] = useState(() => new Set());
  const [inputValues, setInputValues] = useState(() => new Map());
  const [activeInputDate, setActiveInputDate] = useState(null);
  const [attested, setAttested] = useState(false);
  // Whether the attestation box has been interacted with at all, as opposed
  // to its current checked value — so the banner stays visible after an
  // uncheck instead of disappearing the moment the box is unticked again.
  const [attestationTouched, setAttestationTouched] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [attachmentStatuses, setAttachmentStatuses] = useState({});
  const [existingDocuments, setExistingDocuments] = useState([]);
  const [availableDatesByDate, setAvailableDatesByDate] = useState(new Map());
  // Only the rejection of a date click cannot be derived from form state, so
  // it is the one message still held imperatively. Everything else comes out
  // of `getPayRequestErrors`.
  const [actionError, setActionError] = useState(null);
  // Which parts of the form the user has engaged with, so a freshly opened
  // modal is not covered in errors for fields nobody has reached yet.
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadPath, setUploadPath] = useState("");
  const [maxDocSizeMb, setMaxDocSizeMb] = useState(10);
  const [payRequestData, setPayRequestData] = useState(EMPTY_MAP);
  // { type: "PENDING" | "APPROVED", record } while the duplicate modal is open.
  const [duplicate, setDuplicate] = useState(null);

  const previousPayRequestNameRef = useRef(null);
  // The documents this timesheet had when the dialog opened, untouched by
  // anything the user does. See the load effect below.
  const originalDocumentsRef = useRef([]);

  const sortedDates = useMemo(() => Array.from(selectedDates).sort(), [selectedDates]);

  const markTouched = useCallback((field) => {
    setTouched((prev) => (prev[field] ? prev : { ...prev, [field]: true }));
  }, []);

  const {
    fields,
    displayRows,
    estimatedTotal,
    isCalculating,
    description,
    setDescription,
    location,
    payRequestType,
    payRequestName,
    shiftType,
    isAmountContract,
    isShiftContract,
    isNotHoursMinutesContract,
    allTimesheetListData,
    calculationData,
    contractData,
    gracePeriodDays,
  } = useAddPayRequestForm({
    selectedDates: sortedDates,
    inputValues,
    initialTask: task,
    providerId: activeProviderId,
    profileRows,
    selectedProfile,
    onPositionSelect: setSelectedProfile,
    editPositionLabel,
  });

  // Temporary: treat shift contracts and any non-hours/minutes contract the same.
  const isQuantityContract = isShiftContract || isNotHoursMinutesContract;




  const contractMstrKey = shiftType?.contract_mstr_key ?? payRequestName?.contract_mstr_key ?? null;
  // The hook merges the calendar selection with whatever pricing has settled,
  // so the table always mirrors the calendar even mid-debounce.
  const rows = displayRows;
  const multiTierStr = calculationData?.multiTierStr ?? "";
  const rateType = contractData?.[0]?.rate_type ?? null;
  const thresholdUnitType = contractData?.[0]?.thresold_unit_type ?? null;

  // Calculates which dates are disabled based on grace_period_days
  const getGracePeriodDisabledDates = useCallback(
    (monthDates) => {
      if (gracePeriodDays === null || gracePeriodDays === undefined) {
        return new Set();
      }

      const today = moment();
      const disabledByGrace = new Set();

      monthDates.forEach((dateKey) => {
        const dateToCheck = moment(dateKey, "YYYY-MM-DD");

        if (gracePeriodDays >= 0) {
          const daysDifference = today.diff(dateToCheck, "days");
          // daysDifference > 0 means past dates
          // daysDifference < 0 means future dates
          // daysDifference >= 0 && daysDifference <= gracePeriodDays means within grace period
          if (daysDifference < 0 || daysDifference > gracePeriodDays) {
            disabledByGrace.add(dateKey);
          }
        }
      });

      return disabledByGrace;
    },
    [gracePeriodDays]
  );

  // Upload directory + max size come from the same mobile config the app
  // already reads elsewhere.
  useEffect(() => {
    selectMobileConfigCode(cookiesData, dispatch).then((outParams) => {
      setUploadPath(outParams?.doc_dir ?? "");
      const configured = Number(outParams?.allow_max_doc_size);
      if (Number.isFinite(configured) && configured > 0) setMaxDocSizeMb(configured);
    });
  }, [cookiesData, dispatch]);



  /*
   * Load already-attached documents when editing, and keep an untouched copy of
   * what the server had. `existingDocuments` is the live "still attached" list
   * and shrinks as the user removes chips; the snapshot is what we diff it
   * against on save to find the removals. It must be a snapshot rather than a
   * re-fetch: a list read back after uploading would also contain the new
   * files, which would then look like removals and be deleted.
   */
  useEffect(() => {
    if (!task?.timesheet_mstr_key) return;
    getTimesheetDocuments(cookiesData, dispatch, task.timesheet_mstr_key).then((docs) => {
      originalDocumentsRef.current = docs;
      setExistingDocuments(docs);
    });
  }, [task, cookiesData, dispatch]);

  useEffect(() => {
    const timer = setTimeout(() => setFetchMonth(currentMonth), MONTH_FETCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [currentMonth]);

  // Create loads every profile. Edit keeps the timesheet position passed in and only labels it.
  useEffect(() => {
    if (!visible) return undefined;

    let cancelled = false;
    const requestBody = {
      in_tenant_id: cookiesData.out_tenant_id,
      in_user_id: cookiesData.out_user_id,
      in_sort_number: API_CONSTANTS.DEFAULT_SORT_NUMBER,
      in_sort_order: API_CONSTANTS.DEFAULT_SORT_ORDER_ASC,
    };

    POSTAPI(false, SELECT_MOBILE_USER_DETAILS, null, generateHeader(), requestBody, dispatch)
      .then((response) => {
        if (cancelled) return;
        if (response?.data?.out_parameters?.out_ret_code !== API_CONSTANTS.SUCCESS_CODE) return;
        const rows = response?.data?.row_result;
        if (!Array.isArray(rows)) return;
        if (isEdit) {
          // Shown read-only to users with two or more positions, like the Position picker on create
          const editRow = showProfileControl(rows)
            ? positionedRows(rows).find(
                (row) =>
                  String(row.org_position_key) === String(orgPositionKey) &&
                  String(row.provider_mstr_key) === String(providerId)
              )
            : null;
          setEditPositionLabel(editRow ? profileLabel(editRow) : null);
          return;
        }
        setProfileRows(positionedRows(rows));
        // One position selected on the dashboard opens the dialog on it; otherwise today's default.
        const defaultProfile =
          defaultPositionKey == null
            ? null
            : positionedRows(rows).find((row) => String(row.org_position_key) === String(defaultPositionKey));
        setSelectedProfile(defaultProfile ?? pickProfile(rows));
      })
      .catch((error) => {
        console.error("Error fetching mobile user details:", error);
      });

    return () => {
      cancelled = true;
    };
  }, [visible, isEdit, cookiesData, dispatch, defaultPositionKey, orgPositionKey, providerId]);

  // Available dates for the visible month under the selected contract.
  useEffect(() => {
    if (!activeProviderId || !location?.id || !contractMstrKey) {
      setAvailableDatesByDate(new Map());
      return;
    }

    let cancelled = false;
    getAvailablePayRequestDates(cookiesData, dispatch, {
      providerId: activeProviderId,
      locationMstrKey: location.id,
      contractMstrKey,
      monthDate: fetchMonth,
    }).then((rowResult) => {
      if (cancelled) return;
      const dateMap = new Map();
      rowResult.forEach((item) => {
        dateMap.set(item.ts_date, {
          available_hours: item.available_hours ?? 0,
          available_minutes: item.available_minutes ?? 0,
        });
      });
      setAvailableDatesByDate(dateMap);
    });

    return () => {
      cancelled = true;
    };
  }, [activeProviderId, location?.id, contractMstrKey, fetchMonth, cookiesData, dispatch]);

  // Hours already booked in the visible month, so unavailable days can still
  // show what is on them. Uses the non-dispatching service so the dashboard's
  // own calendar state behind the modal is left alone.
  useEffect(() => {
    if (!activeProviderId || !contractMstrKey) {
      setPayRequestData(EMPTY_MAP);
      return;
    }

    let cancelled = false;
    getWorkCalendarForContract(cookiesData, dispatch, {
      providerId: activeProviderId,
      contractMstrKey,
      monthDate: fetchMonth,
    }).then((rows) => {
      if (!cancelled) setPayRequestData(buildPayRequestDataMap(rows));
    });

    return () => {
      cancelled = true;
    };
  }, [activeProviderId, contractMstrKey, fetchMonth, cookiesData, dispatch]);


  // Only a genuine change of pay request name clears the user's work — the
  // initial auto-select must not.
  useEffect(() => {
    if (isEdit) return;

    const currentPayRequestId = payRequestName?.id ?? null;
    if (previousPayRequestNameRef.current !== null && previousPayRequestNameRef.current !== currentPayRequestId) {
      setSelectedDates(new Set());
      setInputValues(new Map());
      setActiveInputDate(null);
      setActionError(null);
    }
    previousPayRequestNameRef.current = currentPayRequestId;
  }, [payRequestName, isEdit]);

  /*
   * The edited timesheet's service date as a calendar key. The task's date
   * arrives in the server timezone; every calendar lookup is in the device's,
   * so the conversion has to happen before comparing.
   */
  const taskDateKey = useMemo(
    () => (task?.ts_date ? moment.tz(task.ts_date, TIME_ZONE).tz(moment.tz.guess()).format("YYYY-MM-DD") : null),
    [task]
  );

  // Prefill the single date being edited. A shift-threshold timesheet carries
  // its quantity in `shifts` rather than hours/minutes. The contract's unit is
  // only known once the contract loads, so the prefill re-runs when it does.
  useEffect(() => {
    if (!taskDateKey) return;
    setSelectedDates(new Set([taskDateKey]));
    const prefillValue = isQuantityContract
      ? String(task?.shifts ?? 0)
      : `${task.hours}:${String(task.minutes).padStart(2, "0")}`;
    setInputValues(new Map([[taskDateKey, prefillValue]]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskDateKey, isQuantityContract]);

  useEffect(() => {
    if (task?.ts_description) setDescription(task.ts_description);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task]);

  /*
   * The contractual selections decide which dates exist at all, so the
   * calendar means nothing until they are all made. Editing arrives with them
   * prefilled and read-only, so it always counts as complete.
   */
  const contractualComplete =
    isEdit ||
    Boolean(location && payRequestType && payRequestName && (!payRequestName.show_shift_type_picker || shiftType));

  // Every day of the visible month that cannot be picked.
  const disabledDates = useMemo(() => {
    const reference = moment(currentMonth);
    const daysInMonth = reference.daysInMonth();
    const allDays = [];
    for (let day = 1; day <= daysInMonth; day += 1) {
      allDays.push(reference.clone().date(day).format("YYYY-MM-DD"));
    }

    // Nothing is selectable until the contractual details are filled in.
    if (!contractualComplete) return new Set(allDays);

    const disabled = new Set();

    // A contract with no availability answer yet is not evidence that every
    // date is closed, so the month is left alone rather than blanket-greyed.
    if (!contractMstrKey || availableDatesByDate.size === 0) return disabled;

    // A pay request cannot be raised against a month that has not started.
    const isFutureMonth = !isEdit && reference.isAfter(moment(), "month");

    // Get dates disabled by grace period
    const graceDisabledDates = getGracePeriodDisabledDates(allDays);

    allDays.forEach((dateKey) => {
      // The date being edited is never closed. Its own hours are what consumed
      // that day's capacity, so the availability API rightly reports nothing
      // left — but disabling the cell also kills pointer events on the time
      // input inside it, leaving the one field edit mode exists to change
      // unreachable.
      if (dateKey === taskDateKey) return;
      if (isFutureMonth || !availableDatesByDate.has(dateKey) || graceDisabledDates.has(dateKey)) {
        disabled.add(dateKey);
      }
    });

    return disabled;
  }, [
    currentMonth,
    availableDatesByDate,
    isEdit,
    contractMstrKey,
    contractualComplete,
    taskDateKey,
    getGracePeriodDisabledDates,
  ]);

  // Total minutes still available on a date; when editing, the task's own time
  // is added back so it can be re-entered.
  const getAvailableMinutesForDate = useCallback(
    (date) => {
      // The edited timesheet's own minutes are part of this date's ceiling
      // whether or not the API still reports spare capacity: a day filled
      // entirely by this request drops out of the response, and without the
      // credit its ceiling would read as zero and block every time entry.
      const ownMinutes = task && taskDateKey === date ? task.hours * 60 + task.minutes : 0;
      const available = availableDatesByDate.get(date);
      if (!available) return ownMinutes;
      return available.available_hours * 60 + available.available_minutes + ownMinutes;
    },
    [availableDatesByDate, task, taskDateKey]
  );

  const handleDateSelect = useCallback(
    (dateKey) => {
      if (isEdit) return;
      if (!contractualComplete) return;

      // Only enforce the allocation check once availability is actually known.
      const hasAvailability = availableDatesByDate.size > 0;

      if (hasAvailability && !selectedDates.has(dateKey) && getAvailableMinutesForDate(dateKey) === 0) {
        setActionError(`Cannot select ${dateKey} - all available hours have been allocated`);
        return;
      }
      setActionError(null);
      // Only reveal the dates rule here. The time rule stays quiet until the
      // user actually reaches the time field (handleInputChange /
      // handleRowTimeChange) or tries to save — otherwise picking a date
      // immediately flashes a "cannot be 0" error before they've had a
      // chance to type anything.
      markTouched("dates");

      setSelectedDates((prev) => {
        const next = new Set(prev);
        if (next.has(dateKey)) next.delete(dateKey);
        else next.add(dateKey);
        return next;
      });

      setInputValues((prev) => {
        if (!prev.has(dateKey)) return prev;
        const next = new Map(prev);
        next.delete(dateKey);
        return next;
      });

      setActiveInputDate((prev) => (prev === dateKey ? null : prev));
    },
    [isEdit, contractualComplete, selectedDates, getAvailableMinutesForDate, availableDatesByDate, markTouched]
  );

  const handleDeselect = useCallback(
    (dateKey) => {
      if (isEdit) return;
      setSelectedDates((prev) => {
        const next = new Set(prev);
        next.delete(dateKey);
        return next;
      });
      setInputValues((prev) => {
        const next = new Map(prev);
        next.delete(dateKey);
        return next;
      });
    },
    [isEdit]
  );

  // Time entry stays editable in edit mode — that is the point of editing.
  const handleInputChange = useCallback(
    (dateKey, value) => {
      setActionError(null);
      markTouched("times");
      setInputValues((prev) => new Map(prev).set(dateKey, value));
    },
    [markTouched]
  );

  const handleInputFocus = useCallback(
    (dateKey) => {
      if (isEdit) return;
      setActiveInputDate(dateKey);
    },
    [isEdit]
  );

  // Edits made in the rate table write back to the same map the calendar reads.
  const handleRowTimeChange = useCallback(
    (dateKey, field, value) => {
      setActionError(null);
      markTouched("times");
      setInputValues((prev) => {
        const current = parseTimeInput(prev.get(dateKey) ?? "");
        const hours = field === "hours" ? value : String(current.hours);
        const minutes = field === "minutes" ? value : String(current.minutes);
        const next = new Map(prev);
        next.set(dateKey, `${String(hours || "0").padStart(2, "0")}:${String(minutes || "0").padStart(2, "0")}`);
        return next;
      });
    },
    [markTouched]
  );

  const handleMonthChange = useCallback(
    (year, month) => {
      setCurrentMonth(new Date(year, month, 1));
      if (!isEdit) {
        setSelectedDates(new Set());
        setInputValues(new Map());
        setActiveInputDate(null);
        setActionError(null);
      }
    },
    [isEdit]
  );

  const handleYearSelect = useCallback((year) => {
    setCurrentMonth((prev) => new Date(year, prev.getMonth(), 1));
  }, []);

  const applyToAllSource = activeInputDate ? (inputValues.get(activeInputDate) ?? "").trim() : "";
  const canApplyToAll = selectedDates.size > 1 && applyToAllSource.length > 0;

  const applyToAllLabel = useMemo(() => {
    if (!applyToAllSource) return "";
    if (isQuantityContract) return applyToAllSource;
    const { hours, minutes } = parseTimeInput(applyToAllSource);
    return formatTimeDisplay(hours, minutes);
  }, [applyToAllSource, isQuantityContract]);

  const handleApplyToAll = useCallback(() => {
    if (!activeInputDate) return;
    const sourceValue = inputValues.get(activeInputDate);
    if (!sourceValue) return;

    setInputValues((prev) => {
      const next = new Map(prev);
      selectedDates.forEach((date) => next.set(date, sourceValue));
      return next;
    });
  }, [activeInputDate, inputValues, selectedDates]);

  // Everything wrong with the form right now, ordered the way the banner
  // should announce it.
  const validationInput = useMemo(
    () => ({
      isEdit,
      location,
      payRequestType,
      payRequestName,
      shiftType,
      selectedDates: sortedDates,
      inputValues,
      rows,
      description,
      getAvailableMinutesForDate,
      isCalculating,
      isAmountContract,
      isShiftContract: isQuantityContract,
      thresholdUnitType,
    }),
    [
      isEdit,
      location,
      payRequestType,
      payRequestName,
      shiftType,
      sortedDates,
      inputValues,
      rows,
      description,
      getAvailableMinutesForDate,
      isCalculating,
      isAmountContract,
      isQuantityContract,
      thresholdUnitType,
    ]
  );

  const errors = useMemo(() => getPayRequestErrors(validationInput), [validationInput]);

  /*
   * An error is announced once its own field has been touched, or once the
   * attestation box has been interacted with at all (checked or unchecked)
   * — that interaction means "I am trying to submit", so from then on the
   * banner has to explain why Save is still disabled, even if the box gets
   * unticked again afterward.
   */
  const visibleError =
    actionError ?? errors.find((error) => attestationTouched || touched[error.field])?.message ?? null;

  const handleDescriptionChange = useCallback(
    (value) => {
      setDescription(value);
    },
    [setDescription]
  );

  const handleDescriptionBlur = useCallback(() => {
    markTouched("description");
  }, [markTouched]);

  const handleAttachmentsAdd = useCallback((files) => {
    setAttachments((prev) => [
      ...prev,
      ...files.map((file, index) => ({
        id: `${file.name}-${file.size}-${prev.length + index}`,
        name: file.name,
        size: file.size,
        file,
      })),
    ]);
  }, []);

  const handleAttachmentRemove = useCallback((id) => {
    setAttachments((prev) => prev.filter((file) => file.id !== id));
    setExistingDocuments((prev) => prev.filter((doc) => doc.document_mstr_key !== id));
  }, []);

  const toast = useCallback((message, type) => dispatch(setNewMessage({ message, type, show: true })), [dispatch]);

  // Attachments upload one at a time, after the timesheets exist.
  const uploadAttachments = useCallback(
    async (timesheetKeys) => {
      for (const attachment of attachments) {
        setAttachmentStatuses((prev) => ({ ...prev, [attachment.id]: "uploading" }));
        // eslint-disable-next-line no-await-in-loop
        const result = await uploadTimesheetDocument(cookiesData, dispatch, {
          file: attachment.file,
          timesheetKeys,
          uploadPath,
        });
        setAttachmentStatuses((prev) => ({
          ...prev,
          [attachment.id]: result.status ? "success" : "error",
        }));
      }
    },
    [attachments, cookiesData, dispatch, uploadPath]
  );

  // The provider's timesheets for the period holding the given date (the first
  // selected date by default).
  const fetchPeriodTimesheets = useCallback((serviceDate = sortedDates[0]) => {
    const contract = contractData?.[0];
    return getTimesheetListByProvider(cookiesData, dispatch, {
      providerId: activeProviderId,
      taskCategoryMstrKey: payRequestType?.id?.toString(),
      periodType: contract?.period_type || "monthly",
      contractStartDate: contract?.contract_start_date || null,
      serviceDate: moment.tz(serviceDate, "YYYY-MM-DD", TIME_ZONE).toDate(),
    });
  }, [contractData, cookiesData, dispatch, activeProviderId, payRequestType, sortedDates]);

  // Same service date (in the timesheet's own timezone), location, pay request
  // type and contract as the one being created.
  const matchesTimesheet = useCallback(
    (ts, serviceDateStr) => {
      if (!location || !payRequestType || !payRequestName) return false;
      const selectedContractMstrKey =
        contractMstrKey ||
        shiftType?.contract_mstr_key ||
        payRequestName?.default_contract_mstr_key ||
        payRequestName?.contract_mstr_key;
      const tsDateInProviderTz = convertTimeZone({
        date: ts.ts_date,
        convertTimeZone: getUserTimezone(),
        inputTimeZone: API_CONSTANTS.DEFAULT_TIMEZONE,
        outputFormat: "yyyy-MM-dd",
      });
      // ids can be strings in the form and numbers on the row
      return (
        tsDateInProviderTz === serviceDateStr &&
        Number(ts.location_mstr_key) === Number(location.id) &&
        Number(ts.contract_type_mstr_key) === Number(payRequestType.id) &&
        Number(ts.contract_mstr_key) === Number(selectedContractMstrKey)
      );
    },
    [location, payRequestType, payRequestName, contractMstrKey, shiftType]
  );

  // A NEW request on the given local date that the hours are merged into
  // (is_updatable). Matches the mobile app: the date is read in the app's
  // timezone then the device's, and either the name's or the shift type's
  // contract is accepted.
  const matchesPendingTimesheet = useCallback(
    (ts, localDate) => {
      if (!location || !payRequestType) return false;
      const contractKeys = [payRequestName?.contract_mstr_key, shiftType?.contract_mstr_key, contractMstrKey].filter(
        (key) => key != null
      );
      const itemDate = moment.tz(ts.ts_date, TIME_ZONE).tz(moment.tz.guess()).format("YYYY-MM-DD");
      return (
        itemDate === localDate &&
        contractKeys.some((key) => Number(ts.contract_mstr_key) === Number(key)) &&
        Number(ts.location_mstr_key) === Number(location.id) &&
        (shiftType?.id == null ||
          ts.shift_type_mstr_key == null ||
          Number(ts.shift_type_mstr_key) === Number(shiftType.id)) &&
        ts.status?.timesheet_status_mstr_key === TimesheetStatus.NEW.mstr_key
      );
    },
    [location, payRequestType, payRequestName, contractMstrKey, shiftType]
  );

  /*
   * Port of checkForDuplicate from the old dialog: an existing timesheet on the
   * same service date (in its own timezone), location, pay request type and
   * contract. A multi-date request bypasses the check entirely.
   */
  const findDuplicate = useCallback(
    (timesheets) => {
      if (isEdit || sortedDates.length !== 1) return null;
      if (!location || !payRequestType || !payRequestName) return null;
      if (!timesheets.length) return null;

      const [serviceDateStr] = sortedDates;
      const matchesCriteria = (ts) => matchesTimesheet(ts, serviceDateStr);

      const statusOf = (ts) => ts.status?.timesheet_status_mstr_key;

      const pendingDuplicate = timesheets.find(
        (ts) => matchesCriteria(ts) && statusOf(ts) === TimesheetStatus.NEW.mstr_key
      );
      if (pendingDuplicate) return { type: "PENDING", record: pendingDuplicate };

      const approvedDuplicate = timesheets.find(
        (ts) => matchesCriteria(ts) && LOCKED_STATUS_KEYS.includes(statusOf(ts))
      );
      if (approvedDuplicate) return { type: "APPROVED", record: approvedDuplicate };

      return null;
    },
    [isEdit, sortedDates, location, payRequestType, payRequestName, matchesTimesheet]
  );

  const handleSave = useCallback(async ({ skipDuplicateCheck = false } = {}) => {
    if (!cookiesData?.out_user_id) {
      toast("UserId not found", "Error");
      return;
    }
    if (!cookiesData?.out_tenant_id) {
      toast("Tenant not found", "Error");
      return;
    }

    /*
     * Last-resort safety net. Save is disabled while any error stands, so this
     * is unreachable from the UI — it only matters if a future caller drives
     * handleSave directly.
     */
    const blocking = getPayRequestErrors(validationInput);
    if (blocking.length > 0) {
      setTouched({
        location: true,
        payRequestType: true,
        payRequestName: true,
        shiftType: true,
        dates: true,
        times: true,
        description: true,
      });
      setActionError(blocking[0].message);
      return;
    }

    if (isEdit && !orgPositionKey) {
      setActionError("Position not found for this pay request");
      return;
    }

    setIsSubmitting(true);

    // Create only: one fresh read serves both the duplicate check and the
    // is_updatable lookup below, so neither depends on a list loaded earlier.
    const latestTimesheets = isEdit ? [] : await fetchPeriodTimesheets();

    if (!skipDuplicateCheck) {
      const found = findDuplicate(latestTimesheets);
      if (found) {
        setDuplicate(found);
        setIsSubmitting(false);
        return;
      }
    }

    if (isEdit) {
      const calculatedRow = rows.find((row) => row.date === taskDateKey);

      const updatedHours = calculatedRow?.hours ?? task?.hours ?? 0;
      const updatedMinutes = calculatedRow?.minutes ?? task?.minutes ?? 0;
      const updatedShifts = calculatedRow?.shifts ?? task?.shifts ?? 0;
      const updatedAmount = calculatedRow?.amount ?? task?.budget ?? 0;
      const updatedRate = calculatedRow?.rate
        ? Number(String(calculatedRow.rate).replace("$", "").trim())
        : task?.rate || 0;

      const currentStatus = task?.status?.timesheet_status_mstr_key;
      const result = await updateTimesheet(cookiesData, dispatch, {
        in_user_id: cookiesData.out_user_id,
        in_tenant_id: cookiesData.out_tenant_id,
        in_device_local_tz: moment.tz.guess(),
        in_timesheets_json: [
          {
            in_device_local_tz: moment.tz.guess(),
            org_position_key: orgPositionKey || 0,
            timesheet_mstr_key: task?.timesheet_mstr_key || 0,
            ts_name: task?.contract_name || "",
            ts_description: description,
            ts_date: task?.ts_date || "",
            hours: isQuantityContract ? 0 : Number(updatedHours) || 0,
            minutes: isQuantityContract ? 0 : Number(updatedMinutes) || 0,
            budget: Number(updatedAmount) || 0,
            status:
              currentStatus === TimesheetStatus.NEW.mstr_key || currentStatus === TimesheetStatus.REJECTED.mstr_key
                ? TimesheetStatus.NEW.mstr_key
                : currentStatus,
            location_mstr_key: task?.location_mstr_key || 0,
            rate: updatedRate,
            shift_type_mstr_key: shiftType?.id ?? task?.shift_type_mstr_key ?? 0,
            ...(isQuantityContract && { shifts: Number(updatedShifts) || 0 }),
          },
        ],
      });

      if (!result.status) {
        // Keep the modal open so the entered work is not lost.
        toast(result.message || "Failed to update pay request", "Error");
        setIsSubmitting(false);
        return;
      }

      /*
       * Delete whatever the user took off the list, comparing the opening
       * snapshot against what is still attached. This runs before the upload so
       * the two steps cannot interfere: nothing added in this session is in the
       * snapshot, so a fresh upload can never be mistaken for a removal.
       */
      const keptKeys = new Set(existingDocuments.map((doc) => doc.document_mstr_key));
      const removed = originalDocumentsRef.current.filter((doc) => !keptKeys.has(doc.document_mstr_key));
      if (removed.length > 0) {
        const results = await Promise.all(
          removed.map((doc) => deleteTimesheetDocument(cookiesData, dispatch, doc.document_mstr_key))
        );
        // The pay request itself is already saved, so a failed delete is worth
        // reporting but not worth failing the save over.
        const failed = results.filter((res) => !res?.status).length;
        if (failed > 0) {
          toast(`${failed} of ${removed.length} attachment(s) could not be removed`, "Error");
        }
      }

      if (attachments.length > 0) {
        await uploadAttachments([task?.timesheet_mstr_key || 0]);
      }

      toast(result.message || "Pay request updated", "Success");
      setIsSubmitting(false);
      onSaved?.();
      onClose?.();
      return;
    }

    // Multi-date add: some dates may already have a NEW request that the hours
    // are merged into. latestTimesheets only covers the period of the first
    // date, so look each date up on its own. A single date goes through the
    // duplicate check instead and needs no flag.
    let pendingCandidates = [];
    if (rows.length > 1) {
      const lists = await Promise.all(
        [...new Set(rows.map((row) => row.date))].map((date) => fetchPeriodTimesheets(date))
      );
      pendingCandidates = [...lists.flat(), ...latestTimesheets];
    }

    const payload = {
      in_user_id: cookiesData.out_user_id,
      in_tenant_id: cookiesData.out_tenant_id,
      in_org_position_key: selectedProfile ? selectedProfile.org_position_key : orgPositionKey || 0,
      in_ts_description: description,
      in_contract_mstr_key: contractMstrKey ?? 0,
      in_ts_name: payRequestName?.title ?? "",
      in_status: TimesheetStatus.NEW.mstr_key,
      in_location_mstr_key: location?.id || 0,
      in_device_local_tz: moment.tz.guess(),
      in_timesheets_json: rows.map((row) => {
        // A pending NEW timesheet on the same day/contract/location/type means
        // the server should update rather than duplicate.
        const localDate = moment.tz(row.date, moment.tz.guess()).format("YYYY-MM-DD");
        const existingPending = pendingCandidates.find((item) => matchesPendingTimesheet(item, localDate));

        return {
          in_ts_date: moment.tz(row.date, moment.tz.guess()).tz(TIME_ZONE).format(DEFAULT_DATE_FORMAT),
          in_hours: Number(row.hours) || 0,
          in_minutes: Number(row.minutes) || 0,
          in_budget: Number(row.amount) || 0,
          in_rate: Number(String(row.rate).replace("$", "").trim()) || 0,
          in_shifts: row.shift?.id ?? shiftType?.id ?? 0,
          ...(isNotHoursMinutesContract && { in_hours: 0, in_minutes: 0, in_shifts: Number(row.shifts) || 0 }),
          ...(existingPending && { is_updatable: 1 }),
        };
      }),
    };

    const result = await createBulkPayRequest(cookiesData, dispatch, payload);

    if (!result.status) {
      // Partial and total failures both keep the modal open.
      const message = result.partial
        ? `Only ${result.successCount} of ${result.successCount + result.failureCount} pay requests were saved. ${result.message}`
        : result.message || "Failed to create pay request";
      toast(message, "Error");
      setActionError(message);
      setIsSubmitting(false);
      return;
    }

    if (result.timesheetKeys.length > 0 && attachments.length > 0) {
      await uploadAttachments(result.timesheetKeys);
    }

    toast(result.message || "Pay request created", "Success");
    setIsSubmitting(false);
    onSaved?.();
    onClose?.();
  }, [
    cookiesData,
    dispatch,
    isEdit,
    task,
    taskDateKey,
    rows,
    description,
    validationInput,
    findDuplicate,
    matchesPendingTimesheet,
    fetchPeriodTimesheets,
    orgPositionKey,
    selectedProfile,
    contractMstrKey,
    payRequestName,
    shiftType,
    isQuantityContract,
    location,
    allTimesheetListData,
    attachments,
    existingDocuments,
    uploadAttachments,
    toast,
    onSaved,
    onClose,
  ]);

  const closeDuplicateModal = useCallback(() => setDuplicate(null), []);

  const handleViewExisting = useCallback(() => {
    const record = duplicate?.record;
    setDuplicate(null);
    if (record) onNavigateToEdit?.(record);
  }, [duplicate, onNavigateToEdit]);

  const handleConfirmDuplicate = useCallback(() => {
    setDuplicate(null);
    handleSave({ skipDuplicateCheck: true });
  }, [handleSave]);

  const allDocuments = useMemo(
    () => [
      ...existingDocuments.map((doc) => ({
        id: doc.document_mstr_key,
        name: doc.document_name,
        size: doc.document_size,
      })),
      ...attachments,
    ],
    [existingDocuments, attachments]
  );

  if (!visible) return null;

  const showDatesBanner = availableDatesByDate.size > 0 && disabledDates.size > 0 && payRequestName && (rateType === "multisite-day" || rateType === "multisite-periodend");
  const saveDisabled = !attested || isSubmitting || isCalculating || errors.length > 0;

  let saveLabel = isEdit ? "Edit Pay Request" : "Add Pay Request";
  if (isCalculating) saveLabel = "Calculating…";
  else if (isSubmitting) saveLabel = "Saving…";

  const duplicateButtons =
    duplicate?.type === "PENDING"
      ? [
          { name: "Close", onClick: closeDuplicateModal },
          { name: "View/Edit Existing", onClick: handleViewExisting },
        ]
      : [
          { name: "No", onClick: closeDuplicateModal },
          { name: "Yes", onClick: handleConfirmDuplicate },
        ];

  return (
    <>
    {duplicate && (
      <DialogComponent
        visible
        onCancelConfirm={closeDuplicateModal}
        title="Duplicate Pay Request Exists"
        isCustomDesc
        customComponent={
          <div className="duplicate-modal-content">
            <p className="duplicate-modal-message">{DUPLICATE_MESSAGES[duplicate.type]}</p>
          </div>
        }
        actionBtn={duplicateButtons}
        width={450}
        height={225}
        className="duplicate-pay-request-modal"
      />
    )}
    <Window
      title={isEdit ? "EDIT PAY REQUEST" : "ADD PAY REQUEST"}
      onClose={onClose}
      className="add-pay-request-dialog"
      modal
      minimizeButton={() => null}
      maximizeButton={() => null}
      // width={1500}
      // height={850}
      initialWidth={Math.min(1800, window.innerWidth * 0.92)}
      initialHeight={Math.min(1200, window.innerHeight * 0.9)}
    >
      <div className="add-pay-request">
        <div className="add-pay-request__column">
          <ContractualDetailsSection
            fields={fields}
            disabled={isEdit}
            visibleError={visibleError}
            onFieldBlur={markTouched}
            isShiftContract={isQuantityContract}
          />

          <div className="add-pay-request__calendar">
            {/* {visibleError && (
                <InfoBanner className={"custom-banner"} attach="bottom" variant="danger" text={visibleError} />
              )} */}

            {showDatesBanner && (
              <InfoBanner
                variant="info"
                className={"custom-banner"}
                attach="top"
                text={`Showing dates for ${payRequestName.title}${
                  location?.title ? ` \u2013 ${location.title}` : ""
                }. Dates for other sites are disabled. Change Pay Request Name to view them.`}
              />
            )}
            <MonthCalendar
              currentDate={currentMonth}
              selectedDates={selectedDates}
              onDateSelect={handleDateSelect}
              onMonthChange={handleMonthChange}
              onYearSelect={handleYearSelect}
              payRequestData={payRequestData}
              inputMode
              inputValues={inputValues}
              onInputChange={handleInputChange}
              onInputFocus={handleInputFocus}
              isLoading={false}
              metricType={isQuantityContract ? QUANTITY_METRIC : HOURS_METRIC}
              disabledDates={disabledDates}
              disableFutureDate={!isEdit}
              decimalInput={isQuantityContract}
              showThresolds
              thresholdUnitType={thresholdUnitType}
            />

            {canApplyToAll && (
              <InfoBanner
                variant="info"
                attach="bottom"
                onClick={handleApplyToAll}
                text={`Apply ${applyToAllLabel} to all the selected dates`}
              />
            )}
          </div>
        </div>

        <div className="add-pay-request__column">
          <TransactionalDetailsSection
            rows={rows}
            estimatedAmount={estimatedTotal.amount}
            estimatedDuration={estimatedTotal.duration}
            onTimeChange={handleRowTimeChange}
            showEstimateNote={!!rows.length}
            onDeselect={handleDeselect}
            disabled={isEdit || isAmountContract}
            isShiftContract={isQuantityContract}
            rateType={rateType}
            multiTierStr={multiTierStr}
            thresholdUnitType={thresholdUnitType}
          />

          <OptionalDetailsSection
            description={description}
            onDescriptionChange={handleDescriptionChange}
            onDescriptionBlur={handleDescriptionBlur}
            attachments={allDocuments}
            onAttachmentsAdd={handleAttachmentsAdd}
            onAttachmentRemove={handleAttachmentRemove}
            attachmentStatuses={attachmentStatuses}
            maxSizeMB={maxDocSizeMb}
            isShiftContract={isQuantityContract}
          />

          <div className="add-pay-request__actions-card">
            <label className="add-pay-request__attestation">
              <input
                type="checkbox"
                checked={attested}
                onChange={(e) => {
                  setAttested(e.target.checked);
                  setAttestationTouched(true);
                }}
                className="add-pay-request__attestation-box"
              />
              <span className="add-pay-request__attestation-text">{ATTESTATION_TEXT}</span>
            </label>

            <div className="add-pay-request__buttons">
              <Button type="button" className="k-button cancel-button" onClick={onClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                type="button"
                className={`k-button button-primary ${saveDisabled ? "disabled" : ""}`}
                disabled={saveDisabled}
                onClick={() => handleSave()}
              >
                {saveLabel}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Window>
    </>
  );
};

export default AddPayRequestDialog;
